'use client'

import {useCallback, useEffect, useRef, useState, type ComponentProps} from 'react'
import {cn} from '@/lib/utils'

type Fade = 'mobile' | 'edges' | 'none'
type Edges = 'none' | 'start' | 'end' | 'both'

// Literal class maps so Tailwind sees every class (see CLAUDE.md § Tailwind class purging).
const FADE_END_CLASS: Record<'mobile' | 'none', string> = {
  // The end edge fades on mobile so a cut-off item reads as "more to scroll".
  mobile:
    '[mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)] sm:[mask-image:none]',
  none: '',
}

// `edges`: fade only the side that has more content, at any width.
const EDGE_FADE_CLASS: Record<Edges, string> = {
  none: '',
  start: '[mask-image:linear-gradient(to_right,transparent,black_40px)]',
  end: '[mask-image:linear-gradient(to_right,black_calc(100%-40px),transparent)]',
  both: '[mask-image:linear-gradient(to_right,transparent,black_40px,black_calc(100%-40px),transparent)]',
}

const edgesOf = (el: HTMLElement): Edges => {
  const start = el.scrollLeft > 1
  const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
  return start && end ? 'both' : start ? 'start' : end ? 'end' : 'none'
}

/**
 * A row that scrolls sideways when its items do not fit (JO2-246).
 *
 * Shared so the product tab strip and the header link row look and behave the
 * same; Jøtul approved sideways scrolling for the category links on mobile
 * (meeting 2026-10-26). Items must be focusable (links, buttons, tabs) so
 * keyboard users can reach the hidden ones: focusing an item scrolls it into
 * view.
 *
 * `fadeEnd`:
 * - `mobile` (default): a fixed fade on the end edge below `sm`.
 * - `edges`: fades follow the scroll position at any width: the start edge
 *   when content is scrolled out to the left, the end edge while there is more
 *   to the right, nothing when it all fits. A fixed end fade covers the last
 *   item once the row is scrolled to the end.
 * - `none`.
 *
 * jotul-only for now. Graduate it to sanity-nextjs-boilerplate when a second
 * clone needs one (no shared boilerplate component scrolls a row sideways as of
 * 2026-10-27).
 */
export function ScrollRow({
  fadeEnd = 'mobile',
  snap = false,
  className,
  children,
  onScroll,
  ...props
}: ComponentProps<'div'> & {fadeEnd?: Fade; snap?: boolean}) {
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState<Edges>('none')
  const tracking = fadeEnd === 'edges'

  const update = useCallback(() => {
    if (ref.current) setEdges(edgesOf(ref.current))
  }, [])

  useEffect(() => {
    if (!tracking || !ref.current) return
    update()
    const observer = new ResizeObserver(update)
    observer.observe(ref.current)
    for (const child of Array.from(ref.current.children)) observer.observe(child)
    return () => observer.disconnect()
  }, [tracking, update])

  return (
    <div
      ref={ref}
      data-slot="scroll-row"
      data-fade-edges={tracking ? edges : undefined}
      className={cn(
        'flex overflow-x-auto [-webkit-overflow-scrolling:touch]',
        tracking ? EDGE_FADE_CLASS[edges] : FADE_END_CLASS[fadeEnd],
        snap && 'snap-x',
        className,
      )}
      onScroll={(event) => {
        if (tracking) update()
        onScroll?.(event)
      }}
      {...props}
    >
      {children}
    </div>
  )
}
